import { Node } from 'reactflow';
import { NodeData } from './types';
import { ActiveHover } from './FlowChart';

const CENTER_NODE_COMPACT_POSITION = 6;
const CENTER_NODE_FIXED_POSITION = 200;
const CENTER_NODE_FIXED_POSITION_HEIGHT = 400;
const VERTICAL_PADDING = 8;

export function layoutNodes(
  initializedNodes: Node<NodeData>[],
  height: number,
  width: number,
) {
  let accumulativeInputHeight = 0;
  let accumulativeOutputHeight = 0;

  return initializedNodes.map((node, index) => {
    const nodeHeight = initializedNodes[index].height || 0;
    const nodeWidth = initializedNodes[index].width || 0;

    if (node.type === 'inputFunction') {
      const newNode = {
        ...node,
        position: { x: 0, y: accumulativeInputHeight },
      };
      accumulativeInputHeight += nodeHeight + VERTICAL_PADDING;
      return newNode;
    }

    if (node.type === 'outputFunction') {
      const newNode = {
        ...node,
        position: { x: width - nodeWidth, y: accumulativeOutputHeight },
      };
      accumulativeOutputHeight += nodeHeight + VERTICAL_PADDING;
      return newNode;
    }

    if (node.type === 'centerFunction') {
      let centerY = Math.max(height / 2 - nodeHeight / 2, 0);
      if (centerY > CENTER_NODE_FIXED_POSITION_HEIGHT) {
        centerY = CENTER_NODE_FIXED_POSITION;
      }
      if (node.data?.alignToTop) {
        centerY = CENTER_NODE_COMPACT_POSITION;
      }
      return {
        ...node,
        position: { x: width / 2 - nodeWidth / 2, y: centerY },
      };
    }

    return node;
  });
}

export function calculateHeight(nodes: Node<unknown>[]) {
  const inputHeight = nodes
    .filter((node) => node.type === 'inputFunction')
    .reduce((acc, node) => {
      return acc + (node.height || 0) + VERTICAL_PADDING;
    }, 0);
  const outputHeight = nodes
    .filter((node) => node.type === 'outputFunction')
    .reduce((acc, node) => {
      return acc + (node.height || 0) + VERTICAL_PADDING;
    }, 0);
  return Math.max(inputHeight, outputHeight);
}

export function getInspectorNodePosition(
  nodes: Node<NodeData>[],
  width: number,
  containerElement: HTMLDivElement | null,
  activeHover?: ActiveHover,
) {
  const centerNode = nodes.find((node) => node.type === 'centerFunction');
  if (!centerNode) {
    return { x: width / 2, y: CENTER_NODE_FIXED_POSITION };
  }

  // Get the flow container element's position
  const containerRect = containerElement?.getBoundingClientRect();
  const containerLeft = containerRect?.left || 0;
  const containerTop = containerRect?.top || 0;

  // Calculate the raw position first
  const rawTop = containerTop + centerNode.position.y + (centerNode.height || 0) + VERTICAL_PADDING;
  let left = containerLeft + centerNode.position.x;

  // Ensure the inspector stays in viewport
  const viewportHeight = window.innerHeight;
  const minTop = 40; // Minimum distance from top of viewport
  const maxTop = viewportHeight - 100; // Maximum distance from top, leaving space at bottom

  // Clamp the top position to keep it in viewport
  const top = Math.min(Math.max(rawTop, minTop), maxTop);

  // If "Edge" is hovered, show inspector on the edge
  if (activeHover?.type === 'edge' && activeHover.hoveredEdge) {
    const align =
      activeHover.hoveredEdge.source === centerNode.id ? 'right' : 'left';
    if (align === 'left') {
      return { top, left };
    }

    left += centerNode.width || 0;
    return { top, left, transform: 'translate(-100%, 0)' };
  }

  // If "Node" is hovered
  else {
    left = containerLeft + centerNode.position.x + (centerNode.width || 0) / 2;
    return { top, left, transform: 'translate(-50%, 0)' };
  }
}
